import {WixSdk} from '@wix/js-sdk-wrapper';
import {Player, PlayerMinMaxDimensions} from '@wix/music-player';
import {WidgetSettings} from '../../typings/settings';
import {LAYOUT_DEFAULTS, LAYOUTS_LIST, LAYOUTS_VERTICAL} from './constants';
import {layoutValues} from './layout-values';
import {isInEditor} from './new/viewMode';

let isNextResizeEnabled = true;

export function resizeOnLayoutChange(settings: WidgetSettings, player: Player, retry = true) {
  const currentLayout = settings.numbers.layout || LAYOUTS_VERTICAL;
  const layoutDefaults = LAYOUT_DEFAULTS[currentLayout];
  const numberTracksShown: number | undefined = layoutDefaults?.defaultNumberTracksShown;
  const currentWidth = layoutDefaults?.defaultWidth || layoutValues[currentLayout].width;

  if (currentLayout === LAYOUTS_VERTICAL && retry) {
    // for vertical layout we first want to resize a width, so the album cover
    // would get its proportion and therefore set its height
    // after that it can calculate the needed height accordingly
    resizeWidgetWidth(settings, currentWidth);
    player.onNextFrame(() => {
      resizeOnLayoutChange(settings, player, false);
      // We need to disable next-time resize, otherwise it will
      // resize with the general rules (expand to full height).
      isNextResizeEnabled = false;
    });
    return;
  }
  const minMaxDimensions = player.getDimensions({minimalTrackCount: numberTracksShown});
  const newDimensions = {
    width: getWidth(settings, minMaxDimensions, currentWidth),
    height: getHeight(settings, minMaxDimensions, numberTracksShown),
  };

  resizeWidget(settings, newDimensions);
}

export function resizeOnReInitNew(settings: WidgetSettings, player: Player, numberTracksShown?: number) {
  let newDimensions;
  if (numberTracksShown) {
    const minMaxDimensions = player.getDimensions({minimalTrackCount: numberTracksShown});
    newDimensions = {
      width: getWidth(settings, minMaxDimensions),
      height: getHeight(settings, minMaxDimensions, numberTracksShown),
    };
  } else {
    newDimensions = player.getDimensionsForFullTracks();
  }

  resizeWidgetNew(settings, newDimensions);
}

function _isFullWidth(settings: WidgetSettings) {
  return settings.booleans.isFullWidth && settings.numbers.layout === LAYOUTS_LIST;
}

function moreThenTolerance(dimension: number) {
  return Math.abs(dimension) > 3;
}

interface Dimensions {
  width?: number;
  height?: number;
}

function resizeWidget(settings: WidgetSettings, newDimensions: Partial<Dimensions>) {
  const {width, height} = newDimensions;
  if (width) {
    settings.rect.width = width;
  }

  if (height) {
    settings.rect.height = height;
  }

  const isResizeAllowed = !_isFullWidth(settings) && WixSdk.Utils.getViewMode() === 'editor';
  if (height && !isResizeAllowed) {
    WixSdk.setHeight(height);
  } else if (width || height) {
    WixSdk.resizeComponent(newDimensions);
  }
}

function resizeWidgetNew(settings: WidgetSettings, newDimensions: Dimensions) {
  const {width: newWidth, height: newHeight} = newDimensions;
  const {width: currentWidth, height: currentHeight} = settings.rect;

  const width = newWidth && moreThenTolerance(newWidth - currentWidth / settings.scale) ? newWidth : undefined;
  const height = newHeight && moreThenTolerance(newHeight - currentHeight / settings.scale) ? newHeight : undefined;

  const isResizeAllowed = !_isFullWidth(settings) && isInEditor();
  if (height && !isResizeAllowed) {
    WixSdk.setHeight(height);
  } else if (width || height) {
    WixSdk.resizeComponent(newDimensions);
  }
}

function resizeWidgetWidth(settings: WidgetSettings, width: number) {
  settings.rect.width = width;
  WixSdk.resizeComponent({width});
}

function getWidth(settings: WidgetSettings, dimensions: PlayerMinMaxDimensions, currentWidth?: number) {
  let width = currentWidth;
  currentWidth = currentWidth || Math.round(settings.rect.width / settings.scale);

  if (dimensions.minWidth && currentWidth < dimensions.minWidth) {
    width = dimensions.minWidth;
  } else if (dimensions.maxWidth && currentWidth > dimensions.maxWidth) {
    width = dimensions.maxWidth;
  }

  return width;
}

function getHeight(settings: WidgetSettings, dimensions: PlayerMinMaxDimensions, numberTracksShown: number) {
  let height;

  const rectHeight = Math.round(settings.rect.height / settings.scale);

  if (rectHeight < dimensions.minHeight || numberTracksShown) {
    height = dimensions.minHeight;
  } else if (rectHeight > dimensions.maxHeight) {
    height = dimensions.maxHeight;
  }

  return height;
}
